import { useEffect, useRef, useState } from "react";
import { usePublishPageLanguage } from "@/lib/page-language";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";

import { createPretestSession } from "@/lib/sessions.functions";
import { rememberTutorRef } from "@/lib/tutor-ref";
import { resetStartSteps, trackFormSubmitted, trackStartStep, trackTestStart } from "@/lib/analytics";
import { looksLikeEmail, missingFields, startErrorCode, type StartStep } from "@/lib/start-funnel";
import { reportStartError } from "@/lib/form-health-client";
import { readUtm } from "@/lib/utm";
import { PRICING_AZN } from "@/lib/payment/pricing";
import { cleanChildName } from "@/lib/format/clean-child-name";
import { TurnstileWidget, type TurnstileHandle } from "@/components/security/TurnstileWidget";
import { GoogleReviewsBadge } from "@/components/site/GoogleReviewsBadge";
import { YolutapLockup } from "@/components/brand/YolutapLogo";
import type { LanguageKey } from "@/lib/yolutap/types";
import { PREFILL_EMAIL_STORAGE_KEY } from "@/edu/guest";

const VALID_AGE_KEYS = ["10_11", "12_13", "14_15", "16_18", "18_22"] as const;

const HEAD_COPY: Record<
  LanguageKey,
  { title: string; description: string; ogDescription: string; ogLocale: string }
> = {
  ru: {
    title: "Начать диагностику — Yolutap",
    description:
      "Пройдите карьерную диагностику Yolutap для вашего ребёнка. Около 30 минут, 10 страниц отчёта, доступ навсегда.",
    ogDescription:
      "Карьерная навигация нового поколения: тест около 30 минут, персональный PDF-отчёт на 10 страниц, три карьерных направления.",
    ogLocale: "ru_RU",
  },
  az: {
    title: "Diaqnostikaya başla — Yolutap",
    description:
      "Uşağınız üçün Yolutap karyera diaqnostikasını keçin. Təxminən 30 dəqiqə, 10 səhifəlik hesabat, həmişəlik giriş.",
    ogDescription:
      "Yeni nəslin karyera naviqasiyası: təxminən 30 dəqiqəlik test, 10 səhifəlik şəxsi PDF hesabat, üç karyera istiqaməti.",
    ogLocale: "az_AZ",
  },
  en: {
    title: "Start the diagnostic — Yolutap",
    description:
      "Take the Yolutap career diagnostic for your child. About 30 minutes, a 10-page report, access forever.",
    ogDescription:
      "Career navigation for a new generation: a test of about 30 minutes, a personal 10-page PDF report, three career directions.",
    ogLocale: "en_US",
  },
};


/** Лимит стартов без пройденной капчи (3 в час с адреса). Введённое остаётся на экране. */
const START_RATE_LIMITED: Record<LanguageKey, string> = {
  ru: "Слишком много попыток с этого устройства за час. Попробуйте позже или напишите нам в WhatsApp +994 51 393 31 97.",
  az: "Bu cihazdan bir saatda çox cəhd olub. Bir az sonra yenidən cəhd edin və ya WhatsApp-a yazın: +994 51 393 31 97.",
  en: "Too many attempts from this device in the last hour. Please try later or message us on WhatsApp +994 51 393 31 97.",
};

/**
 * Проверка не загрузилась (встроенные браузеры Instagram/Facebook, блокировщик).
 * Отправка всё равно проходит — сессия с пометкой captcha_failed, — поэтому
 * не красная тревога, а спокойная подсказка (решение владельца 03.11.2026).
 */
const START_TURNSTILE_HINT: Record<LanguageKey, string> = {
  ru: "Проверка не загрузилась — можно продолжить, нажмите кнопку ниже",
  az: "Yoxlama yüklənmədi — davam edə bilərsiniz, aşağıdakı düyməni basın",
  en: "The check didn't load — you can continue, press the button below",
};

/** Сколько ждать токен капчи после «Подтвердить»; не пришёл — отправка без него (captcha_failed). */
const START_TOKEN_WAIT_MS = 5000;

export const Route = createFileRoute("/start")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { lang?: LanguageKey; pkg?: string; age?: string } => ({
    lang:
      search.lang === "az" || search.lang === "en" || search.lang === "ru"
        ? (search.lang as LanguageKey)
        : undefined,
    pkg: typeof search.pkg === "string" ? search.pkg : undefined,
    // `?age=10_11|12_13|14_15|16_18|18_22` — used by the landing's age
    // tiles to pre-select the age group on arrival. Unknown / missing
    // values fall through to the form's own default.
    age:
      typeof search.age === "string" &&
      (VALID_AGE_KEYS as readonly string[]).includes(search.age)
        ? search.age
        : undefined,
  }),
  // The page renders in the language `?lang` asks for, so the title has to
  // follow it — otherwise an Azerbaijani reader gets an Azerbaijani page under
  // a Russian browser tab. Without the parameter the form itself falls back to
  // Russian, and so does this.
  head: ({ match }) => {
    const h = HEAD_COPY[match.search.lang ?? "ru"];
    return {
      meta: [
        { title: h.title },
        { name: "description", content: h.description },
        { property: "og:title", content: h.title },
        { property: "og:description", content: h.ogDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://yolutap.com/start" },
        { property: "og:locale", content: h.ogLocale },
        { name: "twitter:title", content: h.title },
        { name: "twitter:description", content: h.ogDescription },
        { name: "robots", content: "noindex, nofollow" },
      ],
      links: [{ rel: "canonical", href: "https://yolutap.com/start" }],
    };
  },
  component: StartPage,
});

type AgeKey = "10_11" | "12_13" | "14_15" | "16_18" | "18_22";
type PkgKey = "basic" | "premium";

const LANG_NAMES: Record<LanguageKey, string> = { ru: "Русский", az: "Azərbaycan", en: "English" };

const AGE_RANGES: Record<AgeKey, string> = {
  "10_11": "10–11",
  "12_13": "12–13",
  "14_15": "14–15",
  "16_18": "16–18",
  "18_22": "18–22",
};

const AGE_COPY: Record<LanguageKey, Record<AgeKey, { name: string; stage: string }>> = {
  ru: {
    "10_11": { name: "Открытие", stage: "Игровой формат, образы и фантазии." },
    "12_13": { name: "Поиск", stage: "Первые сильные предпочтения и зоны интереса." },
    "14_15": { name: "Профиль", stage: "Выбор предметов и направления — картируем профиль." },
    "16_18": { name: "Решение", stage: "Поступление и специальность — аргументы за и против." },
    "18_22": { name: "Старт", stage: "Сверка ожиданий с реальностью на первом курсе." },
  },
  az: {
    "10_11": { name: "Kəşf", stage: "Oyun formatı, obrazlar və xəyallar." },
    "12_13": { name: "Axtarış", stage: "İlk güclü üstünlüklər və maraq sahələri." },
    "14_15": { name: "Profil", stage: "Fənlər və istiqamət seçimi — profil xəritəsi." },
    "16_18": { name: "Qərar", stage: "Universitetə qəbul və ixtisas — arqumentlər." },
    "18_22": { name: "Start", stage: "Birinci kursda gözləntilər və reallıq." },
  },
  en: {
    "10_11": { name: "Discovery", stage: "Playful format, images and imagination." },
    "12_13": { name: "Search", stage: "First strong preferences and areas of interest." },
    "14_15": { name: "Profile", stage: "Subject and direction choice — mapping the profile." },
    "16_18": {
      name: "Decision",
      stage: "University and specialisation — arguments for and against.",
    },
    "18_22": { name: "Start", stage: "Aligning expectations with reality in the first year." },
  },
};

const PKG_COPY: Record<LanguageKey, Record<PkgKey, { desc: string; feats: string[] }>> = {
  ru: {
    basic: {
      desc: "Полный тест и PDF-отчёт на 10 страниц с архетипом, сильными сторонами и тремя карьерными направлениями.",
      feats: [
        "Полная диагностика по 3 теориям",
        "PDF-отчёт + рекомендации",
        "Доступ в кабинете — навсегда",
      ],
    },
    premium: {
      desc: "Всё из Basic — плюс часовая сессия с карьерным коучем, разбор отчёта и индивидуальный план следующих шагов.",
      feats: ["Всё из Basic", "Сессия с коучем (60 мин)", "Индивидуальный план"],
    },
  },
  az: {
    basic: {
      desc: "3 nəzəriyyə üzrə tam diaqnostika. Arxetip, güclü tərəflər və üç karyera istiqaməti ilə 10 səhifəlik PDF hesabat.",
      feats: [
        "3 nəzəriyyə üzrə tam diaqnostika",
        "PDF hesabat + tövsiyələr",
        "Kabinetə həmişəlik giriş",
      ],
    },
    premium: {
      desc: "Basic-in hamısı — üstəgəl karyera kouçu ilə saatlıq sessiya, hesabatın təhlili və fərdi növbəti addımlar planı.",
      feats: ["Basic-in hamısı", "Kouçla sessiya (60 dəq)", "Fərdi plan"],
    },
  },
  en: {
    basic: {
      desc: "Full assessment across 3 theories. 10-page PDF report with archetype, strengths, and three career directions.",
      feats: [
        "Full assessment across 3 theories",
        "PDF report + recommendations",
        "Lifetime access in your account",
      ],
    },
    premium: {
      desc: "Everything in Basic — plus a one-hour session with a career coach, report walkthrough, and a personalised next-steps plan.",
      feats: ["Everything in Basic", "Coach session (60 min)", "Personalised plan"],
    },
  },
};

// price values are derived from PRICING_AZN (single source of truth shared
// with PayScreen + the AzeriCard signing path). was/save are pure
// marketing-discount metadata that lives only in the /start tier picker.
//
// НЕОБЯЗАТЕЛЬНЫЕ НАМЕРЕННО. Тариф без акции — это тариф без старой цены и
// без процента, а не тариф, у которого старая цена равна новой: «250 ₼
// зачёркнуто, 250 ₼» выглядит поломкой, а «−0%» ею и является. Premium с
// 2026-09-08 продаётся по полной цене, у Basic акция 49 → 35 живёт дальше.
// Процент скидки — одна формула для бейджа тарифа и строки «Экономия» в
// итоге (27.10.2026: наверху стояло «−28%» руками, внизу считалось −29%).
const pctOff = (was: number, price: number) => Math.round(((was - price) / was) * 100);
const BASIC_PRICE = parseFloat(PRICING_AZN.basic.amount);
const PKG_META: Record<PkgKey, { tone: string; price: number; was?: number; save?: string }> = {
  basic: { tone: "Basic", price: BASIC_PRICE, was: 49, save: `−${pctOff(49, BASIC_PRICE)}%` },
  premium: { tone: "Premium", price: parseFloat(PRICING_AZN.premium.amount) },
};

const T = {
  ru: {
    navHelp: "Есть вопрос?",
    navBack: "Назад на главную",
    eyebrow: "Начало диагностики",
    h1a: "Несколько ",
    h1em: "деталей",
    h1b: " — и можно начинать.",
    lede: "Заполните короткую форму: язык теста, возраст ребёнка и выбранный пакет. После — сразу приступаем к диагностике, отчёт придёт на ваш email.",
    factMin: "минут на тест",
    factPg: "страниц отчёта",
    factAccess: "доступ навсегда",
    s1num: "01",
    s1h: "Язык ",
    s1hem: "теста",
    hint1: "Все вопросы и отчёт будут на выбранном языке.",
    ariaLang: "Язык",
    s2num: "02",
    s2h: "Возрастная ",
    s2hem: "группа",
    hint2: "Вопросы и тон отчёта меняются под возраст. Выберите фактический возраст ребёнка.",
    diag18: "Диагностика 18+ · отдельный формат · RU / AZ / EN",
    s3num: "03",
    s3h: "О ",
    s3hem: "ребёнке",
    hint3:
      "Имя появится на первой странице отчёта. Укажите хотя бы один email — по нему откроется доступ к кабинету и результату.",
    nameLabel: "Имя ребёнка",
    namePh: "Например, Симузар",
    nameInvalidMsg:
      "Введите имя ребёнка (только буквы, 2–20 символов). Email-адрес сюда писать не нужно.",
    emailLabel: "Email родителя",
    emailHint:
      "Контактный адрес: сюда придут чек и отчёт. Если проходишь сам — укажи почту родителя, а пока её нет, свою.",
    s4num: "04",
    s4h: "Выберите ",
    s4hem: "пакет",
    hint4: "Можно повысить пакет позже из личного кабинета.",
    recommended: "★ Рекомендуем",
    consentPre: "Я согласен(-на) с ",
    consentLink: "политикой обработки данных",
    consentPost:
      " и понимаю, что отчёт будет отправлен на указанный e-mail. Если мне есть 18 лет — подтверждаю от своего имени; если участнику нет 18 лет — подтверждаю как его родитель или законный представитель.",
    submitIdle: "Пройти тест",
    submitBusy: "Загружаем тест…",
    next: "Далее",
    consentNeeded: "Поставьте галочку согласия выше — без неё нельзя продолжить.",
    reviewTitle: "Проверьте заказ",
    reviewIntro:
      "Сейчас будет создана сессия и предложена оплата. После создания изменить эти данные нельзя.",
    back: "Назад",
    confirm: "Подтвердить и продолжить к оплате",
    confirmBusy: "Создаём сессию…",
    errLanguageRequired: "Выберите язык теста.",
    errAgeRequired: "Выберите возраст ребёнка.",
    errorDefault: "Не удалось создать сессию. Попробуйте ещё раз или напишите нам в WhatsApp +994 51 393 31 97.",
    errorCaptcha: "Пожалуйста, подтвердите, что вы не робот.",
    sumTitle: "Ваш ",
    sumTitleEm: "заказ",
    sumLang: "Язык",
    sumAge: "Возраст",
    sumChild: "Ребёнок",
    sumEmail: "Email",
    sumPkg: "Пакет",
    sumEmpty: "не указано",
    sumTotal: "Итого",
    sumSave: (n: number, pct: number) => `Экономия ${n} ₼ · −${pct}%`,
    perks: [
      "Разовый платёж — без подписки и автосписаний",
      "Доступ к отчёту в кабинете навсегда",
    ],
    payNote:
      "Платёж проходит по защищённому каналу. Yolutap не хранит данные карты — обрабатывает платёжный оператор.",
    footPolicy: "Политика конфиденциальности",
    footTerms: "Условия",
    footContact: "Контакты",
    logoAriaLabel: "Yolutap — на главную",
  },
  az: {
    navHelp: "Sualınız var?",
    navBack: "Ana səhifəyə qayıt",
    eyebrow: "Diaqnostikanın başlanğıcı",
    h1a: "Bir neçə ",
    h1em: "detal",
    h1b: " — və başlaya bilərik.",
    lede: "Qısa formu doldurun: test dili, uşağın yaşı və seçilmiş paket. Sonra — diaqnostikaya başlayırıq, hesabat e-poçtunuza gələcək.",
    factMin: "dəqiqə test",
    factPg: "səhifə hesabat",
    factAccess: "həmişəlik giriş",
    s1num: "01",
    s1h: "Testin ",
    s1hem: "dili",
    hint1: "Bütün suallar və hesabat seçilmiş dildə olacaq.",
    ariaLang: "Dil",
    s2num: "02",
    s2h: "Yaş ",
    s2hem: "qrupu",
    hint2: "Suallar və hesabatın tonu yaşa görə dəyişir. Uşağın faktiki yaşını seçin.",
    diag18: "18+ diaqnostikası · ayrı format · RU / AZ / EN",
    s3num: "03",
    s3h: "Uşaq ",
    s3hem: "haqqında",
    hint3:
      "Ad hesabatın birinci səhifəsində görünəcək. Ən azı bir e-poçt yazın — kabinetə və nəticəyə giriş onunla açılacaq.",
    nameLabel: "Uşağın adı",
    namePh: "Məsələn, Aynur",
    nameInvalidMsg:
      "Uşağın adını yazın (yalnız hərflər, 2–20 simvol). Email ünvanı yazmayın.",
    emailLabel: "Valideynin e-poçtu",
    emailHint:
      "Əlaqə ünvanı: qəbz və hesabat bura gələcək. Özün keçirsənsə — valideynin e-poçtunu yaz, hələ yoxdursa, öz ünvanını.",
    s4num: "04",
    s4h: "Paket ",
    s4hem: "seçin",
    hint4: "Paketi sonradan şəxsi kabinetdən artıra bilərsiniz.",
    recommended: "★ Tövsiyə edirik",
    consentPre: "Mən ",
    consentLink: "məlumatların emalı siyasəti",
    consentPost:
      " ilə razıyam və hesabatın göstərilən e-poçta göndəriləcəyini başa düşürəm. 18 yaşım tamamdırsa, razılığı öz adımdan verirəm; iştirakçı 18 yaşından kiçikdirsə, onun valideyni və ya qanuni nümayəndəsi kimi razılıq verirəm.",
    submitIdle: "Testi keç",
    submitBusy: "Test yüklənir…",
    next: "Növbəti",
    consentNeeded: "Davam etmək üçün yuxarıdakı razılıq qutusunu işarələyin.",
    reviewTitle: "Sifarişi yoxlayın",
    reviewIntro:
      "İndi sessiya yaradılacaq və ödəniş təklif olunacaq. Yaradıldıqdan sonra bu məlumatları dəyişmək olmur.",
    back: "Geri",
    confirm: "Təsdiqlə və ödənişə keç",
    confirmBusy: "Sessiya yaradılır…",
    errLanguageRequired: "Test dilini seçin.",
    errAgeRequired: "Uşağın yaşını seçin.",
    errorDefault: "Sessiya yaradıla bilmədi. Yenidən cəhd edin və ya WhatsApp-a yazın: +994 51 393 31 97.",
    errorCaptcha: "Zəhmət olmasa robot olmadığınızı təsdiqləyin.",
    sumTitle: "Sifarişiniz",
    sumTitleEm: "",
    sumLang: "Dil",
    sumAge: "Yaş",
    sumChild: "Uşaq",
    sumEmail: "E-poçt",
    sumPkg: "Paket",
    sumEmpty: "göstərilməyib",
    sumTotal: "Cəmi",
    sumSave: (n: number, pct: number) => `Qənaət ${n} ₼ · −${pct}%`,
    perks: [
      "Birdəfəlik ödəniş — abunə yoxdur",
      "Hesabata kabinetdə həmişəlik giriş",
    ],
    payNote:
      "Ödəniş təhlükəsiz kanaldan keçir. Yolutap kart məlumatlarını saxlamır — ödəniş operatoru tərəfindən emal edilir.",
    footPolicy: "Məxfilik siyasəti",
    footTerms: "Şərtlər",
    footContact: "Əlaqə",
    logoAriaLabel: "Yolutap — ana səhifəyə",
  },
  en: {
    navHelp: "Have a question?",
    navBack: "Back to home",
    eyebrow: "Start your assessment",
    h1a: "A few ",
    h1em: "details",
    h1b: " — then we're ready to go.",
    lede: "Fill in the short form: test language, child's age, and selected package. Then we start the assessment right away, and the report will be sent to your email.",
    factMin: "min test",
    factPg: "page report",
    factAccess: "lifetime access",
    s1num: "01",
    s1h: "Test ",
    s1hem: "language",
    hint1: "All questions and the report will be in the selected language.",
    ariaLang: "Language",
    s2num: "02",
    s2h: "Age ",
    s2hem: "group",
    hint2: "Questions and report tone adapt to the child's age. Select the child's actual age.",
    diag18: "18+ assessment · separate format · RU / AZ / EN",
    s3num: "03",
    s3h: "About the ",
    s3hem: "child",
    hint3:
      "The name will appear on the first page of the report. Give at least one email — it is what opens the cabinet and the result.",
    nameLabel: "Child's name",
    namePh: "E.g. Sofia",
    nameInvalidMsg:
      "Please enter the child's first name (letters only, 2–20 characters). Don't paste an email address here.",
    emailLabel: "Parent's email",
    emailHint:
      "Contact address: the receipt and the report go here. Taking it yourself? Give a parent's email — or your own until you have one.",
    s4num: "04",
    s4h: "Choose a ",
    s4hem: "package",
    hint4: "You can upgrade your package later from your account.",
    recommended: "★ Recommended",
    consentPre: "I agree to the ",
    consentLink: "data processing policy",
    consentPost:
      " and understand that the report will be sent to the indicated email. If I am 18 or older, I confirm on my own behalf; if the participant is under 18, I confirm as their parent or legal guardian.",
    submitIdle: "Take the test",
    submitBusy: "Loading test…",
    next: "Next",
    consentNeeded: "Tick the consent box above to continue.",
    reviewTitle: "Review your order",
    reviewIntro:
      "A session is about to be created and payment will be offered. After creation these details can't be changed.",
    back: "Back",
    confirm: "Confirm and proceed to payment",
    confirmBusy: "Creating session…",
    errLanguageRequired: "Please choose a test language.",
    errAgeRequired: "Please choose the child's age.",
    errorDefault: "Could not create session. Please try again or message us on WhatsApp +994 51 393 31 97.",
    errorCaptcha: "Please confirm you are not a robot.",
    sumTitle: "Your ",
    sumTitleEm: "order",
    sumLang: "Language",
    sumAge: "Age",
    sumChild: "Child",
    sumEmail: "Email",
    sumPkg: "Package",
    sumEmpty: "not specified",
    sumTotal: "Total",
    sumSave: (n: number, pct: number) => `You save ${n} ₼ · −${pct}%`,
    perks: [
      "One-time payment — no subscriptions",
      "Lifetime access to the report in your account",
    ],
    payNote:
      "Payment goes through a secure channel. Yolutap does not store card data — it is processed by the payment operator.",
    footPolicy: "Privacy Policy",
    footTerms: "Terms",
    footContact: "Contact",
    logoAriaLabel: "Yolutap — back to home",
  },
} as const;

const LogoIcon = ({ size = 32 }: { size?: number }) => (
  <svg
    style={{ width: size, height: size, display: "block" }}
    viewBox="0 0 240 240"
    aria-hidden="true"
  >
    <g transform="translate(20, 20) rotate(-6, 50, 50)">
      <path
        fill="#1E5A6B"
        d="M0 0 L100 0 L100 35 C118 35 118 65 100 65 L100 100 L65 100 C65 118 35 118 35 100 L0 100 Z"
      />
    </g>
    <g transform="translate(125, 14) rotate(8, 50, 50)">
      <path
        fill="#F0A63A"
        d="M0 0 L100 0 L100 100 L65 100 C65 118 35 118 35 100 L0 100 L0 65 C-18 65 -18 35 0 35 Z"
      />
    </g>
    <g transform="translate(14, 130) rotate(-3, 50, 50)">
      <path
        fill="#4BAE8C"
        d="M0 0 L35 0 C35 -18 65 -18 65 0 L100 0 L100 35 C118 35 118 65 100 65 L100 100 L0 100 Z"
      />
    </g>
    <g transform="translate(130, 132) rotate(6, 50, 50)">
      <path
        fill="#7A5AAE"
        d="M0 0 L35 0 C35 -18 65 -18 65 0 L100 0 L100 100 L0 100 L0 65 C-18 65 -18 35 0 35 Z"
      />
    </g>
  </svg>
);

function StartPage() {
  const navigate = useNavigate();
  const submit = useServerFn(createPretestSession);

  const { lang: langFromUrl, pkg: pkgFromUrl, age: ageFromUrl } = Route.useSearch();

  // Language + ageGroup deliberately start "" when there's no URL hint.
  // Landing's tiles deep-link with ?lang=/?age= so users coming through
  // the marketing site keep their pre-selected values; direct visitors
  // to /start (shared link, bookmark, ad without query params) now MUST
  // pick both before submit — same fix as the redeem flow (PR #31,
  // Javid 2026-06-21). Silent default 14_15 / "ru" caused parents to
  // route children into the wrong test.
  const [language, setLanguage] = useState<LanguageKey | "">(langFromUrl ?? "");
  const [ageGroup, setAgeGroup] = useState<AgeKey | "">(
    (ageFromUrl as AgeKey | undefined) ?? "",
  );
  const [childName, setChildName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  // ?ref= из ссылки репетитора запоминается сразу: validateSearch его не держит,
  // и к отправке формы в адресе его уже может не быть.
  useEffect(() => {
    rememberTutorRef(window.location.search);
  }, []);
  // Почта из подбора вузов без кабинета (поп-ап «Пройти тест»): едет через
  // sessionStorage, а не через адрес. Подставляется только в пустое поле и
  // сразу удаляется — ни логика формы, ни проверка не меняются.
  useEffect(() => {
    try {
      const v = window.sessionStorage.getItem(PREFILL_EMAIL_STORAGE_KEY);
      if (v === null) return;
      window.sessionStorage.removeItem(PREFILL_EMAIL_STORAGE_KEY);
      if (v.trim()) setParentEmail((cur) => (cur.trim() ? cur : v.trim()));
    } catch {
      /* хранилище недоступно — поле остаётся пустым */
    }
  }, []);
  const [childEmail, setChildEmail] = useState("");
  const [showChildEmail, setShowChildEmail] = useState(false);
  const [packageType, setPackageType] = useState<PkgKey>(
    pkgFromUrl === "premium" ? "premium" : "basic",
  );
  // Галочку ставит сам человек (27.10.2026): заранее не стоит, без неё «Далее» неактивна.
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  // Two-step flow: "form" collects data, "review" shows a read-only
  // summary with [Back] and [Confirm]. The createPretestSession RPC
  // fires only from "review" → Confirm — so a parent who chose the
  // wrong age can spot it and back-track before any DB row exists.
  const [step, setStep] = useState<"form" | "review">("form");
  // Turnstile token from <TurnstileWidget />. Null until the user
  // completes the (usually invisible) challenge. Re-set to null after a
  // submit-attempt so a fresh token is fetched on retry (tokens are
  // single-use server-side).
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  const ts = useRef<TurnstileHandle>(null);

  // Falls back to "ru" while language is unselected so the page is
  // still readable on first paint. Once the parent clicks a language
  // chip the form switches; the test itself uses `language` directly
  // (never the fallback) because the RPC requires the real choice.
  const displayLanguage: LanguageKey = (language || "ru") as LanguageKey;
  // Клик по языку переводит форму на месте — <html lang> за ней.
  usePublishPageLanguage(displayLanguage);
  const t = T[displayLanguage];
  // Воронка /start по шагам в GA (28.10.2026, src/lib/start-funnel.ts): язык и
  // пакет, один раз на шаг за визит. Ни почты, ни имени в параметрах.
  const funnel = (s: StartStep, extra: { lang?: string; ok?: boolean; reason?: string; code?: string; preset_lang?: string; preset_age?: string } = {}) =>
    trackStartStep(s, { lang: displayLanguage, pkg: packageType, ...extra });
  useEffect(() => {
    resetStartSteps();
    // С чем пришли из ссылки (?lang=, ?age=): тогда start_lang/start_age без клика не будет.
    funnel("start_view", { preset_lang: langFromUrl ?? "", preset_age: ageFromUrl ?? "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Student-email copy — kept local (small, trilingual) rather than threaded
  // through the large T dict. Mirrors the /redeem child_email field.
  //
  // It used to be labelled "(optional)" and sat under a mandatory "parent's
  // email", which is how the first live user — a seventeen-year-old taking the
  // test for himself — ended up filing his own address as a parent's and being
  // handed a parent's cabinet. The two fields now read as a pair: whoever the
  // student is goes in the student's field, and that is what decides the role
  // when they later sign in.
  const childEmailCopy = {
    ru: {
      label: "Email ученика",
      hint: "По ней ученик войдёт в свой кабинет и увидит свой результат.",
    },
    az: {
      label: "Şagirdin e-poçtu",
      hint: "Şagird onunla öz kabinetinə girəcək və nəticəsini görəcək.",
    },
    en: {
      label: "Student's email",
      hint: "The student signs in with it and sees their own result.",
    },
  }[displayLanguage];
  const ageCopy = AGE_COPY[displayLanguage];
  const pkgCopy = PKG_COPY[displayLanguage];

  const selectedPkgMeta = PKG_META[packageType];
  // Одно условие на всю акцию: есть старая цена — есть и зачёркивание, и
  // процент, и строка «вы экономите». Нет — нет ничего из трёх.
  const hasCampaign = typeof selectedPkgMeta.was === "number";
  const saved = hasCampaign ? selectedPkgMeta.was! - selectedPkgMeta.price : 0;
  const savedPct = hasCampaign ? pctOff(selectedPkgMeta.was!, selectedPkgMeta.price) : 0;

  // Step 1: form submit → validate → transition to review.
  // No network call here. The session is NOT created yet.
  function onFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // «Далее» в GA: ok и чего не хватает (lang, age, name, email, consent).
    const miss = missingFields({ language, ageGroup, nameOk: cleanChildName(childName).ok, email: parentEmail, consent });
    funnel("start_next_click", miss.length ? { ok: false, reason: miss.join(",") } : { ok: true });
    // Без галочки кнопка выглядит неактивной (aria-disabled), подсказка под ней уже видна.
    if (!consent) {
      document.getElementById("sp-consent")?.focus();
      return;
    }
    let hasError = false;
    // W1 fix: clean the child-name on submit. Rejects empty / short input,
    // strips email-paste / digits / control chars, caps at 20 chars. The
    // PDF cover renders this value verbatim — any garbage here ends up on
    // page 1 of the parent's report.
    const cleaned = cleanChildName(childName);
    if (!cleaned.ok) {
      setNameError(true);
      hasError = true;
    }
    // Формат — как на сервере (z.email()): раньше кривой адрес падал только на подтверждении.
    if (!looksLikeEmail(parentEmail)) {
      setEmailError(true);
      hasError = true;
    }
    if (hasError) return;
    if (language === "") {
      setError(t.errLanguageRequired);
      return;
    }
    if (ageGroup === "") {
      setError(t.errAgeRequired);
      return;
    }
    // Капча — на экране подтверждения, прямо перед созданием сессии (24.10.2026):
    // токен с этого шага успевал сгореть (300 с), пока человек читал сводку.
    setStep("review");
  }

  // Step 2: confirm on review → fire the RPC that creates the session
  // and navigate to /session/<token>. This is the ONLY place
  // createPretestSession is called.
  //
  // Path B is now fully wired end-to-end: createPretestSession accepts
  // 18_22; loadLocalizedChildQuestions branches on test_version_id and
  // flattens the 18_22 JSON blocks into LocalizedQuestion (incl. scale
  // block 6 with poles); /session/<token>/test handles both
  // multiple_choice and scale question_type; validateChildAnswers +
  // saveChildProgress know about 18_22's question_count; finalize hook
  // routes to renderReportPdf + sendReportEmail with isAdult=true and
  // the report-ready-adult email template; result/print dispatches to
  // Report13Page when is1822=true. /diag1822 remains as a stand-alone
  // playground.
  async function onConfirm() {
    setError(null);
    if (
      language === "" ||
      ageGroup === "" ||
      !consent
    ) {
      // Defensive — review screen shouldn't be reachable with these
      // missing, but if state is somehow corrupt we want to fall back
      // cleanly to the form instead of sending a malformed RPC.
      setStep("form");
      return;
    }
    const cleaned = cleanChildName(childName);
    const cleanedName = cleaned.ok ? cleaned.value : childName.trim();

    setBusy(true);
    try {
      // Свежий токен прямо перед отправкой; виджет один раз тихо повторяет
      // ошибку. Не прошла и после этого (встроенные браузеры, 600010/110600) —
      // отправляем без токена: сервер создаст сессию с пометкой captcha_failed
      // (лимит 3 в час с адреса, sessions.functions.ts). Из-за капчи никто не теряется.
      // Ждём токен не дольше START_TOKEN_WAIT_MS (было 20 с: в Facebook in-app
      // люди не дожидались «Отправляем…», прод 03.11.2026).
      const tok = (await ts.current?.freshToken(undefined, START_TOKEN_WAIT_MS)) ?? turnstileToken ?? null;
      const session = await submit({
        data: {
          child_name: cleanedName,
          age_group: ageGroup as "10_11" | "12_13" | "14_15" | "16_18" | "18_22",
          language_key: language as LanguageKey,
          parent_email: parentEmail.trim(),
          ...(childEmail.trim()
            ? { child_email: childEmail.trim().toLowerCase() }
            : {}),
          parent_consent: true as const,
          package_type: packageType,
          turnstile_token: tok ?? undefined,
          // «Кто привёл» из ссылки репетитора (?ref=) — только учёт, тест и цена те же.
          ref: rememberTutorRef(window.location.search) ?? undefined,
          // utm с первого захода на сайт (src/lib/utm.ts); нет или хранилище недоступно — пусто.
          ...readUtm(),
        },
      });
      // Land on the session root — SessionLayout renders PayScreen for
      // pending_payment sessions, which native-form-POSTs to the real
      // /api/payment/azericard-start endpoint. The previous auto-mock-pay
      // shortcut was removed (see sessions.functions.ts).
      // Анкета отправлена и сессия создана — первое событие, которое до сих
      // пор существовало только в базе. Без него в аналитике не с чем
      // сравнивать: видно, сколько людей пришло, и не видно, сколько дошло.
      trackFormSubmitted({ ageGroup: ageGroup, language: language, tier: packageType });
      // test_start — то же мгновение под именем, которое просил владелец (25.10.2026).
      trackTestStart({ ageGroup: ageGroup, language: language, tier: packageType });
      navigate({ to: "/session/$token", params: { token: session.access_token } });
    } catch (err) {
      const m = err instanceof Error ? err.message : "";
      // В Sentry — каждая ошибка, кодом воронки (form=start, submit_reason), как abroad_start.
      reportStartError(err);
      funnel("start_error", { code: startErrorCode(err) });
      // Сбой базы или сети — своим текстом на языке формы: сообщение базы
      // («new row violates check constraint…») человеку ничего не говорит (e2e 03.11.2026).
      const code = startErrorCode(err);
      setError(
        code === "rate_limited"
          ? START_RATE_LIMITED[displayLanguage]
          : code === "server" || code === "network" || !m
            ? t.errorDefault
            : m,
      );
    } finally {
      setBusy(false);
    }
  }

  function onBack() {
    setStep("form");
    setError(null);
  }

  return (
    <div
      style={{
        fontFamily: '"Nunito Sans", system-ui, sans-serif',
        background: "#FBFAF7",
        color: "#0F2A38",
        minHeight: "100vh",
      }}
    >
      {/* NAV */}
      <div className="sp-nav-outer">
        <div className="sp-wrap">
          <nav className="sp-nav">
            <div className="sp-nav-left">
              <a
                href="/"
                className="sp-logo"
                aria-label={t.logoAriaLabel}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                <YolutapLockup />
              </a>
            </div>
            <div className="sp-nav-right">
              <a href="/#contact" className="sp-help-link">
                {t.navHelp}
              </a>
              <a
                href="/"
                className="sp-btn sp-btn-ghost"
                // 44, а не 40: ниже этого палец промахивается. Высота стоит
                // здесь инлайном, поэтому и правится здесь — правило в CSS
                // инлайн не перебьёт, а ставить ради этого !important значит
                // оставить следующему человеку загадку.
                style={{ height: 44, fontSize: "13.5px", padding: "0 18px" }}
              >
                {t.navBack}
              </a>
            </div>
          </nav>
        </div>
      </div>

      {/* HEADER */}
      <header className="sp-head">
        <div className="sp-wrap">
          <div className="sp-head-grid">
            <div>
              <div className="sp-eyebrow">{t.eyebrow}</div>
              <h1 className="sp-h1">
                {t.h1a}
                <em>{t.h1em}</em>
                {t.h1b}
              </h1>
              <GoogleReviewsBadge lang={displayLanguage} placement="start_head" />
            </div>
            <div className="sp-head-lede">
              <p>{t.lede}</p>
              <div className="sp-factrow">
                <div className="sp-fact">
                  <span className="sp-fact-v">~30</span>
                  <span className="sp-fact-l">{t.factMin}</span>
                </div>
                <div className="sp-fact">
                  <span className="sp-fact-v">10</span>
                  <span className="sp-fact-l">{t.factPg}</span>
                </div>
                <div className="sp-fact">
                  <span className="sp-fact-v">∞</span>
                  <span className="sp-fact-l">{t.factAccess}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN */}
      <main className="sp-main">
        <div className="sp-wrap">
          <div className="sp-main-grid">
            {/* FORM COLUMN — renders either the data-entry form or the
                read-only review card depending on `step`. The summary
                sidebar (see <aside> below) is shown on both steps. */}
            {step === "form" && (
            <form className="sp-form-col" onSubmit={onFormSubmit}>
              {/* 1. LANGUAGE */}
              <section className="sp-section">
                <div className="sp-s-head">
                  <span className="sp-s-num">{t.s1num}</span>
                  <h2 className="sp-s-h2">
                    {t.s1h}
                    <em>{t.s1hem}</em>
                  </h2>
                </div>
                <p className="sp-s-hint">{t.hint1}</p>
                <div className="sp-seg" role="radiogroup" aria-label={t.ariaLang}>
                  {(["ru", "az", "en"] as LanguageKey[]).map((l) => (
                    <button
                      key={l}
                      type="button"
                      className={`sp-seg-btn${language === l ? " on" : ""}`}
                      onClick={() => {
                        setLanguage(l);
                        funnel("start_lang", { lang: l });
                      }}
                    >
                      {LANG_NAMES[l]}
                    </button>
                  ))}
                </div>
              </section>

              {/* 2. AGE */}
              <section className="sp-section">
                <div className="sp-s-head">
                  <span className="sp-s-num">{t.s2num}</span>
                  <h2 className="sp-s-h2">
                    {t.s2h}
                    <em>{t.s2hem}</em>
                  </h2>
                </div>
                <p className="sp-s-hint">{t.hint2}</p>
                <div className="sp-age-grid">
                  {(["10_11", "12_13", "14_15", "16_18", "18_22"] as AgeKey[]).map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={`sp-age-pick${ageGroup === key ? " on" : ""}`}
                      onClick={() => {
                        setAgeGroup(key);
                        funnel("start_age");
                      }}
                    >
                      <span className="sp-age-n">
                        {AGE_RANGES[key].split("–")[0]}
                        <small>–{AGE_RANGES[key].split("–")[1]}</small>
                      </span>
                      <span className="sp-age-lbl">{ageCopy[key].name}</span>
                      <span className="sp-age-stage">{ageCopy[key].stage}</span>
                    </button>
                  ))}
                </div>
                {ageGroup && <p className="sp-age-note">{ageCopy[ageGroup].stage}</p>}
                {ageGroup === "18_22" && (
                  <p className="sp-s-hint" style={{ marginTop: 4 }}>
                    {t.diag18}
                  </p>
                )}
              </section>

              {/* 3. ABOUT CHILD */}
              <section className="sp-section">
                <div className="sp-s-head">
                  <span className="sp-s-num">{t.s3num}</span>
                  <h2 className="sp-s-h2">
                    {t.s3h}
                    <em>{t.s3hem}</em>
                  </h2>
                </div>
                <p className="sp-s-hint">{t.hint3}</p>
                <div className="sp-fields">
                  <div className="sp-field">
                    <label htmlFor="sp-name">{t.nameLabel}</label>
                    <input
                      id="sp-name"
                      type="text"
                      placeholder={t.namePh}
                      autoComplete="given-name"
                      maxLength={20}
                      value={childName}
                      className={nameError ? "error" : ""}
                      onBlur={(e) => e.target.value.trim() && funnel("start_name")}
                      onChange={(e) => {
                        setChildName(e.target.value);
                        if (nameError) setNameError(false);
                      }}
                    />
                    {nameError && (
                      <p
                        role="alert"
                        style={{
                          marginTop: 6,
                          fontSize: 12,
                          color: "var(--destructive, #b00020)",
                          lineHeight: 1.4,
                        }}
                      >
                        {t.nameInvalidMsg}
                      </p>
                    )}
                  </div>
                  <div className="sp-field">
                    <label htmlFor="sp-email">{t.emailLabel}</label>
                    <input
                      id="sp-email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      value={parentEmail}
                      className={emailError ? "error" : ""}
                      onBlur={(e) => looksLikeEmail(e.target.value) && funnel("start_email")}
                      onChange={(e) => {
                        setParentEmail(e.target.value);
                        if (emailError) setEmailError(false);
                      }}
                    />
                    <span className="sp-field-hint">{t.emailHint}</span>
                  </div>
                  {/* Почта ученика (27.10.2026) — по ссылке «+ Email ученика»: на
                      первом экране одно обязательное поле родителя. Что ученик
                      войдёт в свой кабинет по своей почте — как раньше: child_email
                      уходит в createPretestSession, если поле заполнено. */}
                  {showChildEmail ? (
                    <div className="sp-field">
                      <label htmlFor="sp-child-email">{childEmailCopy.label}</label>
                      <input
                        id="sp-child-email"
                        type="email"
                        placeholder="student@example.com"
                        autoComplete="off"
                        value={childEmail}
                        onChange={(e) => setChildEmail(e.target.value)}
                      />
                      <span className="sp-field-hint">{childEmailCopy.hint}</span>
                    </div>
                  ) : (
                    <button type="button" className="sp-add-email" onClick={() => setShowChildEmail(true)}>
                      + {childEmailCopy.label}
                    </button>
                  )}
                </div>
              </section>

              {/* 4. PACKAGE */}
              <section className="sp-section">
                <div className="sp-s-head">
                  <span className="sp-s-num">{t.s4num}</span>
                  <h2 className="sp-s-h2">
                    {t.s4h}
                    <em>{t.s4hem}</em>
                  </h2>
                </div>
                <p className="sp-s-hint">{t.hint4}</p>
                <div className="sp-pkg-grid">
                  {(["basic", "premium"] as PkgKey[]).map((key) => {
                    const meta = PKG_META[key];
                    const copy = pkgCopy[key];
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`sp-pkg${key === "premium" ? " prem" : ""}${packageType === key ? " on" : ""}`}
                        onClick={() => setPackageType(key)}
                      >
                        <span className="sp-pkg-radio" />
                        <div className="sp-pkg-head-pad">
                          {/* Бейдж переехал ВНУТРЬ шапки и стал строкой над
                              названием. Снаружи он был абсолютным и накрывал
                              то, что подвернётся: цену на 390 и 414,
                              название на 320. У карточки без бейджа стоит
                              пустой двойник — иначе на широком экране её
                              название оказывается на строку выше соседнего. */}
                          {key === "premium" && pkgFromUrl !== "basic" ? (
                            <span className="sp-pkg-badge">{t.recommended}</span>
                          ) : (
                            <span className="sp-pkg-badge sp-pkg-badge-hold" aria-hidden="true">
                              {t.recommended}
                            </span>
                          )}
                          <div className="sp-pkg-h">
                            <div className="sp-pkg-name">
                              Yolutap<span className="sp-pkg-tone">{meta.tone}</span>
                            </div>
                            <div className="sp-pkg-price">
                              {meta.was !== undefined && (
                                <span className="sp-pkg-was">{meta.was} ₼</span>
                              )}
                              <span className="sp-pkg-n">{meta.price}</span>
                              <span className="sp-pkg-c">₼</span>
                              {meta.save !== undefined && (
                                <span className="sp-pkg-save">{meta.save}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <p className="sp-pkg-desc">{copy.desc}</p>
                        <ul className="sp-pkg-feats">
                          {copy.feats.map((f) => (
                            <li key={f}>{f}</li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>
                {/* Coach intro link — only meaningful when the user is
                    on / considering the premium (Test + Kouç) package,
                    so we show it for both options but pin it to the
                    package-section bottom. /coaches itself routes to
                    the right locale (cookie-aware redirect for /coaches;
                    direct routes for /az/coaches and /en/coaches). */}
                <div style={{ marginTop: 14, textAlign: "center" }}>
                  <a
                    href={displayLanguage === "ru" ? "/coaches" : `/${displayLanguage}/coaches`}
                    className="sp-coach-link"
                    style={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: "var(--teal, #1E5A6B)",
                      textDecoration: "underline",
                      textUnderlineOffset: 3,
                    }}
                  >
                    {displayLanguage === "ru"
                      ? "Познакомьтесь с коучем"
                      : displayLanguage === "az"
                        ? "Kouçla tanış olun"
                        : "Meet the coach"}{" "}
                    →
                  </a>
                </div>
              </section>

              {/* CONSENT */}
              <div className="sp-consent">
                <input
                  id="sp-consent"
                  type="checkbox"
                  className="sp-consent-cb"
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    if (e.target.checked) funnel("start_consent");
                  }}
                />
                <label htmlFor="sp-consent">
                  {t.consentPre}
                  <a href={displayLanguage === "ru" ? "/privacy" : `/${displayLanguage}/privacy`}>{t.consentLink}</a>
                  {t.consentPost}
                </label>
              </div>

              {error && <div className="sp-error">{error}</div>}


              {/* CTA */}
              <div className="sp-cta-row">
                <GoogleReviewsBadge lang={displayLanguage} placement="start_cta" size="sm" />
                <button
                  type="submit"
                  aria-disabled={!consent || undefined}
                  aria-describedby={consent ? undefined : "sp-consent-needed"}
                  className="sp-btn sp-btn-primary"
                  style={{ height: 54, padding: "0 32px", fontSize: 16, marginLeft: "auto" }}
                >
                  {t.next} <span className="sp-btn-arr">→</span>
                </button>
              </div>
              {!consent && (
                <p id="sp-consent-needed" className="sp-consent-needed">
                  {t.consentNeeded}
                </p>
              )}
            </form>
            )}

            {step === "review" && (
              <div
                className="sp-form-col"
                role="region"
                aria-label={t.reviewTitle}
              >
                <section className="sp-section">
                  <div className="sp-s-head">
                    <h2 className="sp-s-h2">{t.reviewTitle}</h2>
                  </div>
                  <p className="sp-s-hint" style={{ marginBottom: 16 }}>
                    {t.reviewIntro}
                  </p>

                  <div
                    className="sp-sum-rows"
                    style={{
                      background: "#FBFAF7",
                      border: "1px solid #E3E7EB",
                      borderRadius: 12,
                      padding: "16px 18px",
                    }}
                  >
                    <div className="sp-sum-row">
                      <span className="sp-sum-lab">{t.sumLang}</span>
                      <span className="sp-sum-val">
                        {language ? LANG_NAMES[language as LanguageKey] : t.sumEmpty}
                      </span>
                    </div>
                    <div className="sp-sum-row">
                      <span className="sp-sum-lab">{t.sumAge}</span>
                      <span className="sp-sum-val">
                        {ageGroup ? AGE_RANGES[ageGroup as AgeKey] : t.sumEmpty}
                      </span>
                    </div>
                    <div className="sp-sum-row">
                      <span className="sp-sum-lab">{t.sumChild}</span>
                      <span className="sp-sum-val">{childName.trim() || t.sumEmpty}</span>
                    </div>
                    {/* Both addresses, each named. The old summary showed one
                        row labelled "Email" — which of the two it meant was
                        exactly the thing the person needed to check. */}
                    {childEmail.trim() && (
                      <div className="sp-sum-row">
                        <span className="sp-sum-lab">{childEmailCopy.label}</span>
                        <span className="sp-sum-val" style={{ wordBreak: "break-all" }}>
                          {childEmail.trim()}
                        </span>
                      </div>
                    )}
                    <div className="sp-sum-row">
                      <span className="sp-sum-lab">{t.emailLabel}</span>
                      <span className="sp-sum-val" style={{ wordBreak: "break-all" }}>
                        {parentEmail.trim() || t.sumEmpty}
                      </span>
                    </div>
                    <div className="sp-sum-row">
                      <span className="sp-sum-lab">{t.sumPkg}</span>
                      <span className="sp-sum-val">Yolutap {selectedPkgMeta.tone}</span>
                    </div>
                    <div
                      className="sp-sum-row"
                      style={{ borderTop: "1px solid #E3E7EB", paddingTop: 8, marginTop: 8 }}
                    >
                      <span className="sp-sum-lab">{t.sumTotal}</span>
                      <span className="sp-sum-val" style={{ fontWeight: 700 }}>
                        {selectedPkgMeta.price} ₼
                      </span>
                    </div>
                  </div>

                  {error && (
                    <div className="sp-error" style={{ marginTop: 16 }}>
                      {error}
                    </div>
                  )}

                  {/* Turnstile — на экране подтверждения: токен берётся прямо перед createPretestSession. */}
                  <div style={{ marginTop: 16 }}>
                    <TurnstileWidget
                      handleRef={ts}
                      retryOnce
                      // Свой текст ниже вместо красного запасного: отправка без капчи проходит.
                      fallback={false}
                      onToken={(t) => {
                        setTurnstileToken(t);
                        setTurnstileFailed(false);
                      }}
                      onExpired={() => setTurnstileToken(null)}
                      onError={() => {
                        setTurnstileToken(null);
                        setTurnstileFailed(true);
                      }}
                    />
                    {turnstileFailed && (
                      <p data-turnstile-hint style={{ margin: "8px 0 0", fontSize: 14, lineHeight: 1.45, color: "#5B6B73" }}>
                        {START_TURNSTILE_HINT[displayLanguage]}
                      </p>
                    )}
                  </div>

                  <div
                    className="sp-cta-row"
                    style={{ display: "flex", gap: 12, marginTop: 24 }}
                  >
                    <button
                      type="button"
                      onClick={onBack}
                      disabled={busy}
                      className="sp-btn sp-btn-ghost"
                      style={{
                        height: 54,
                        padding: "0 24px",
                        fontSize: 15,
                      }}
                    >
                      ← {t.back}
                    </button>
                    <button
                      type="button"
                      onClick={onConfirm}
                      disabled={busy}
                      className="sp-btn sp-btn-primary"
                      style={{
                        height: 54,
                        padding: "0 24px",
                        fontSize: 15,
                        flex: 1,
                      }}
                    >
                      {busy ? t.confirmBusy : t.confirm}
                    </button>
                  </div>
                </section>
              </div>
            )}

            {/* SUMMARY SIDEBAR */}
            <aside
              className="sp-summary"
              aria-label={t.sumTitle + (t.sumTitleEm ? " " + t.sumTitleEm : "")}
            >
              {/* Trust kadr at top of summary — warm photo of parent +
                  teen reviewing something on a phone together. Reassures
                  before payment. Sized as a banner via .sp-summary-photo. */}
              <picture className="sp-summary-photo">
                <source
                  type="image/webp"
                  srcSet="/photos/parent_teen_phone_02-400.webp 400w, /photos/parent_teen_phone_02-760.webp 760w"
                  sizes="380px"
                />
                <img
                  src="/photos/parent_teen_phone_02.jpg"
                  alt={
                    displayLanguage === "ru"
                      ? "Мама и дочь-подросток вместе смотрят в телефон дома"
                      : displayLanguage === "az"
                        ? "Ana və yeniyetmə qızı evdə birlikdə telefona baxır"
                        : "A mother and her teenage daughter looking at a phone together at home"
                  }
                  width={760}
                  height={760}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <h3>
                  {t.sumTitle}
                  {t.sumTitleEm ? <em>{t.sumTitleEm}</em> : null}
                </h3>
                <LogoIcon size={24} />
              </div>

              <div className="sp-sum-rows">
                <div className="sp-sum-row">
                  <span className="sp-sum-lab">{t.sumLang}</span>
                  <span className={`sp-sum-val${language ? "" : " ph"}`}>
                    {language ? LANG_NAMES[language as LanguageKey] : t.sumEmpty}
                  </span>
                </div>
                <div className="sp-sum-row">
                  <span className="sp-sum-lab">{t.sumAge}</span>
                  <span className={`sp-sum-val${ageGroup ? "" : " ph"}`}>
                    {ageGroup ? AGE_RANGES[ageGroup as AgeKey] : t.sumEmpty}
                  </span>
                </div>
                <div className="sp-sum-row">
                  <span className="sp-sum-lab">{t.sumChild}</span>
                  <span className={`sp-sum-val${childName.trim() ? "" : " ph"}`}>
                    {childName.trim() || t.sumEmpty}
                  </span>
                </div>
                {childEmail.trim() && (
                  <div className="sp-sum-row">
                    <span className="sp-sum-lab">{childEmailCopy.label}</span>
                    <span className="sp-sum-val" style={{ wordBreak: "break-all" }}>
                      {childEmail.trim()}
                    </span>
                  </div>
                )}
                <div className="sp-sum-row">
                  <span className="sp-sum-lab">{t.emailLabel}</span>
                  <span
                    className={`sp-sum-val${parentEmail.trim() ? "" : " ph"}`}
                    style={{ wordBreak: "break-all" }}
                  >
                    {parentEmail.trim() || t.sumEmpty}
                  </span>
                </div>
                <div className="sp-sum-row">
                  <span className="sp-sum-lab">{t.sumPkg}</span>
                  <span className="sp-sum-val">Yolutap {selectedPkgMeta.tone}</span>
                </div>
              </div>

              <hr className="sp-sum-rule" />

              <div className="sp-sum-total">
                <span className="sp-sum-total-lab">{t.sumTotal}</span>
                <span className="sp-sum-price-row">
                  {hasCampaign && (
                    <span className="sp-sum-was">{selectedPkgMeta.was} ₼</span>
                  )}
                  <span className="sp-sum-n">{selectedPkgMeta.price}</span>
                  <span className="sp-sum-c">₼</span>
                </span>
              </div>
              {hasCampaign && (
                <span className="sp-sum-save">{t.sumSave(saved, savedPct)}</span>
              )}

              <div className="sp-sum-perks">
                {t.perks.map((p) => (
                  <div key={p} className="sp-sum-perk">
                    {p}
                  </div>
                ))}
              </div>

              <div className="sp-sum-note">{t.payNote}</div>
            </aside>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="sp-footer">
        <div className="sp-wrap">
          <p
            style={{
              fontSize: "11.5px",
              color: "#6B8090",
              lineHeight: 1.55,
              marginBottom: 14,
              paddingBottom: 14,
              borderBottom: "1px solid #E3E7EB",
            }}
          >
            {displayLanguage === "ru"
              ? "«YOLUTAP» MMC · VÖEN: 1309994231 · AZ1065, г. Баку, Ясамальский р-н, пр. Н. Нариманова, д. 127, кв. 36 · Тел: +994 51 393 31 97 · support@yolutap.com"
              : displayLanguage === "az"
                ? "«YOLUTAP» MMC · VÖEN: 1309994231 · AZ1065, Bakı şəhəri, Yasamal rayonu, N. Nərimanov pr. 127, mənzil 36 · Tel: +994 51 393 31 97 · support@yolutap.com"
                : '"YOLUTAP" LLC · TIN (VÖEN): 1309994231 · AZ1065, Baku, Yasamal district, N. Narimanov ave. 127, apt. 36 · Tel: +994 51 393 31 97 · support@yolutap.com'}
          </p>
          <div className="sp-foot">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <a
                href="/"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  textDecoration: "none",
                }}
              >
                <LogoIcon size={22} />
                <span className="sp-wm" style={{ fontSize: 18 }}>
                  Y<span className="sp-o">o</span>lutap
                </span>
              </a>
              <span className="sp-foot-copy">
                {displayLanguage === "en" ? '© 2026 "YOLUTAP" LLC' : "© 2026 «YOLUTAP» MMC"}
              </span>
            </div>
            <nav className="sp-foot-nav">
              <a href={displayLanguage === "ru" ? "/privacy" : `/${displayLanguage}/privacy`}>{t.footPolicy}</a>
              <a href={displayLanguage === "ru" ? "/terms" : `/${displayLanguage}/terms`}>{t.footTerms}</a>
              <a href="/#contact">{t.footContact}</a>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
